import type { TaxTypeDetail } from "./types/multiTax.types";

const CACHE_PREFIX = "multitax-tax-type-details:v1";

interface CacheKeyParts {
    accountId?: string | number | null;
    userId?: string | number | null;
    companyId?: string | number | null;
}

const cleanPart = (value: string | number | null | undefined, fallback: string): string =>
    value === null || value === undefined || value === "" ? fallback : String(value);

/**
 * Builds the session-storage cache key for the TaxType details tree.
 *
 * The key is scoped to (accountId, userId, companyId). A company has exactly
 * one country so `companyId` is already sufficient for uniqueness — there is
 * no need to include a country segment in the key.
 */
export const buildTaxTypeDetailsCacheKey = ({
    accountId,
    userId,
    companyId,
}: CacheKeyParts): string | null => {
    if (companyId === null || companyId === undefined || companyId === "") return null;

    return [
        CACHE_PREFIX,
        cleanPart(accountId, "no-account"),
        cleanPart(userId, "no-user"),
        String(companyId),
    ].join(":");
};

const normalizeTaxTypeDetails = (details: TaxTypeDetail[]): TaxTypeDetail[] =>
    details.map(tt => ({
        taxTypeId: tt.taxTypeId,
        taxTypeIdSK: tt.taxTypeIdSK ?? null,
        taxSubTypeDetails: (tt.taxSubTypeDetails ?? []).map(st => ({
            taxSubTypeId: st.taxSubTypeId,
            taxSubTypeIdSK: st.taxSubTypeIdSK ?? null,
            taxTypeMappingId: st.taxTypeMappingId,
            jurisdictions: (st.jurisdictions ?? []).map(j => ({
                country: j.country,
                region: j.region,
                jurisTypeId: j.jurisTypeId,
                jurisdictionTypeId: j.jurisdictionTypeId || j.jurisTypeId,
                jurisCode: j.jurisCode,
                jurisName: j.jurisName,
                shortName: j.shortName ?? null,
            })),
        })),
    }));

export const readTaxTypeDetailsSessionCache = (key: string | null): TaxTypeDetail[] | null => {
    if (!key) return null;

    try {
        const raw = sessionStorage.getItem(key);
        if (!raw) return null;
        const parsed: unknown = JSON.parse(raw);
        return Array.isArray(parsed) ? (parsed as TaxTypeDetail[]) : null;
    } catch {
        return null;
    }
};

export const writeTaxTypeDetailsSessionCache = (
    key: string | null,
    details: TaxTypeDetail[]
): void => {
    if (!key) return;

    try {
        sessionStorage.setItem(key, JSON.stringify(normalizeTaxTypeDetails(details)));
    } catch {
        // Ignore quota/private-mode failures; Redux still holds data for the current page.
    }
};

export const clearTaxTypeDetailsSessionCache = (): void => {
    try {
        const keys: string[] = [];
        for (let i = 0; i < sessionStorage.length; i += 1) {
            const key = sessionStorage.key(i);
            if (key?.startsWith(CACHE_PREFIX)) {
                keys.push(key);
            }
        }
        keys.forEach(key => sessionStorage.removeItem(key));
    } catch {
        // Ignore storage access failures during logout/session cleanup.
    }
};
